import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const cleanXmlString = (value) => {
  if (value === null || value === undefined) return "";

  // Convert non-string values to string
  if (typeof value !== "string") {
    value = String(value);
  }

  // Remove control characters and invalid unicode for Excel
  value = value.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\uFFFE\uFFFF]/g, "");

  // Remove special Word characters (like smart quotes, non-breaking spaces, etc.)
  value = value
    .replace(/[\u2018\u2019\u201A\u201B\u2039\u203A]/g, "'") // single quotes
    .replace(/[\u201C\u201D\u201E\u201F\u00AB\u00BB]/g, '"') // double quotes
    .replace(/\u00A0/g, " "); // non-breaking space

  // Excel cell limit: 32,767 characters
  /*  if (value.length > 32767) {
    value = value.substring(0, 32767);
  } */

  return value;
};

export const exportToExcelJS = async (
  rows,
  columns,
  filename = "Exported_data",
  ignore_last_col = false,
) => {
  if (ignore_last_col) {
    columns.pop();
  }
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Sheet 1");

  //Ajouter du titre au premier lignes
  worksheet.mergeCells(1, 1, 1, columns.length);
  const titre = worksheet.getCell("A1");
  titre.value = "teste";
  titre.font = { bold: true, size: 16 };
  titre.alignment = { vertical: "middle", horizontal: "center" };

  // Définir les colonnes avec entêtes
  worksheet.columns = columns.map((col) => ({
    header: col.headerName,
    key: col.field,
    width: col.width / 4 || 10,
  }));

  worksheet.views = [{ state: "frozen", ySplit: 2, showGridLines: false }]; // Geler la première ligne et caher les lignes de grille

  // Activer le filtre sur les entêtes (ligne 2)
  worksheet.autoFilter = {
    from: { row: 2, column: 1 },
    to: { row: 2, column: columns.length },
  };

  //ajouter titre
  const headerRow1 = worksheet.getRow(1);
  headerRow1.getCell(1).value = filename;

  // Ajouter l'en-tête
  if (rows.length > 0) {
    const headerRow = worksheet.getRow(2);
    columns.forEach((col) => {
      headerRow.getCell(col.field).value = col.headerName;
    });
  }

  // Style de l'en-tête
  const headerRow2 = worksheet.getRow(2);
  headerRow2.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 13 };
    cell.height = 20;
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF4F81BD" }, // bleu foncé
    };
    cell.alignment = { vertical: "middle", horizontal: "center" };
    cell.border = {
      top: { style: "thin", color: { argb: "000000" } },
      bottom: { style: "thin", color: { argb: "000000" } },
      left: { style: "thin", color: { argb: "000000" } },
      right: { style: "thin", color: { argb: "000000" } },
    };
  });

  // Ajouter les données
  rows.forEach((row) => {
    const rowData = {};
    columns.forEach((col) => {
      rowData[col.field] = cleanXmlString(row[col.field]);
    });
    worksheet.addRow(rowData);
  });

  worksheet.eachRow((row) => {
    if (row.number === 1) return;

    // Ajouter une bordure uniquement sur les 5 premières colonnes si l'entête a une taille de 5
    for (let i = 1; i <= columns.length; i++) {
      const cell = row.getCell(i);
      cell.border = {
        top: { style: "thin", color: { argb: "000000" } },
        bottom: { style: "thin", color: { argb: "000000" } },
        left: { style: "thin", color: { argb: "000000" } },
        right: { style: "thin", color: { argb: "000000" } },
      };
    }

    row.eachCell((cell) => {
      cell.alignment = {
        vertical: "middle",
        wrapText: true,
      };
      cell.border = {
        top: { style: "thin", color: { argb: "000000" } },
        bottom: { style: "thin", color: { argb: "000000" } },
        left: { style: "thin", color: { argb: "000000" } },
        right: { style: "thin", color: { argb: "000000" } },
      };
    });
  });

  // Générer et télécharger
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/octet-stream" });
  saveAs(blob, `${filename}.xlsx`);
};

export const exportMultipleSheetsToExcelJS = async (
  sheetsData,
  filename = "Exported_data_multiple_sheets",
) => {
  const workbook = new ExcelJS.Workbook();
  const sheetNames = Object.keys(sheetsData);

  sheetNames.forEach((sheetName) => {
    const worksheet = workbook.addWorksheet(sheetName);
    const rows = sheetsData[sheetName];
    const columns =
      sheetsData[sheetName].length > 0
        ? Object.keys(sheetsData[sheetName][0]).map((key) => ({
            headerName: String(key).replace(/_/g, " ").toUpperCase(),
            field: key,
          }))
        : [];

    //Ajouter du titre au premier lignes
    worksheet.mergeCells(1, 1, 1, columns.length);
    const titre = worksheet.getCell("A1");
    titre.value = "teste";
    titre.font = { bold: true, size: 16 };
    titre.alignment = { vertical: "middle", horizontal: "center" };

    // Définir les colonnes avec entêtes
    worksheet.columns = columns.map((col) => ({
      header: col.headerName,
      key: col.field,
      width: col.width / 4 || 40,
    }));

    worksheet.views = [{ state: "frozen", ySplit: 2, showGridLines: false }]; // Geler la première ligne et caher les lignes de grille

    // Activer le filtre sur les entêtes (ligne 2)
    worksheet.autoFilter = {
      from: { row: 2, column: 1 },
      to: { row: 2, column: columns.length },
    };

    //ajouter titre
    const headerRow1 = worksheet.getRow(1);
    headerRow1.getCell(1).value = filename;

    // Ajouter l'en-tête
    if (rows.length > 0) {
      const headerRow = worksheet.getRow(2);
      columns.forEach((col) => {
        headerRow.getCell(col.field).value = col.headerName;
      });
    }

    // Style de l'en-tête
    const headerRow2 = worksheet.getRow(2);
    headerRow2.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 13 };
      cell.height = 20;
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF4F81BD" }, // bleu foncé
      };
      cell.alignment = { vertical: "middle", horizontal: "center" };
      cell.border = {
        top: { style: "thin", color: { argb: "000000" } },
        bottom: { style: "thin", color: { argb: "000000" } },
        left: { style: "thin", color: { argb: "000000" } },
        right: { style: "thin", color: { argb: "000000" } },
      };
    });

    // Ajouter les données
    rows.forEach((row) => {
      const rowData = {};
      columns.forEach((col) => {
        rowData[col.field] = cleanXmlString(row[col.field]);
      });
      worksheet.addRow(rowData);
    });

    worksheet.eachRow((row) => {
      if (row.number === 1) return;

      // Ajouter une bordure uniquement sur les 5 premières colonnes si l'entête a une taille de 5
      for (let i = 1; i <= columns.length; i++) {
        const cell = row.getCell(i);
        cell.border = {
          top: { style: "thin", color: { argb: "000000" } },
          bottom: { style: "thin", color: { argb: "000000" } },
          left: { style: "thin", color: { argb: "000000" } },
          right: { style: "thin", color: { argb: "000000" } },
        };
      }

      row.eachCell((cell) => {
        cell.alignment = {
          vertical: "middle",
          wrapText: true,
        };
        cell.border = {
          top: { style: "thin", color: { argb: "000000" } },
          bottom: { style: "thin", color: { argb: "000000" } },
          left: { style: "thin", color: { argb: "000000" } },
          right: { style: "thin", color: { argb: "000000" } },
        };
      });
    });
  });

  // Générer et télécharger
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/octet-stream" });
  saveAs(blob, `${filename}.xlsx`);
};

export const transformeDataHeaderToExcelColonne = (data) => {
  if (!data || data.length === 0) return [];
  const headers = Object.keys(data[0]);
  return headers.map((header) => ({
    headerName: String(header).replace(/_/g, " ").toUpperCase(),
    field: header,
  }));
};
